import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import RoleCard from "@/components/blueprint/RoleCard";
import ExecutiveDashboard from "@/components/blueprint/ExecutiveDashboard";
import CapacityAnalysis from "@/components/blueprint/CapacityAnalysis";
import Roadmap from "@/components/blueprint/Roadmap";
import SavingsChart from "@/components/blueprint/SavingsChart";
import ConsultationCTA from "@/components/ConsultationCTA";
import { METHOD_NOTE } from "@/lib/advisor";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getBlueprint, downloadBlueprintPdf, requestPdfCopy, fmtMoney, fmtInvestment } from "@/lib/api";
import { HOME_URL } from "@/lib/constants";
import { PRICING_DISCLAIMER } from "@/lib/constants";
import {
  Loader2,
  Sparkles,
  TrendingDown,
  Wallet,
  Users,
  ArrowLeft,
  Download,
  Mail,
  Clock,
  Target,
  FileText,
} from "lucide-react";

export default function Blueprint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pdfToken = searchParams.get("pdf_token");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [expiredOpen, setExpiredOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    getBlueprint(id)
      .then(setData)
      .catch(() => setError(true));
  }, [id]);

  const handleDownload = async () => {
    if (!pdfToken) {
      setExpiredOpen(true);
      return;
    }
    setDownloading(true);
    try {
      const res = await downloadBlueprintPdf(id, pdfToken);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const company = (data?.result?.industry_label || "Company").replace(/[^a-zA-Z0-9]+/g, "-");
      const date = new Date().toISOString().slice(0, 10);
      a.download = `Rendeavored-Workforce-Blueprint-${company}-${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Your blueprint PDF is downloading");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410 || status === 401) {
        setExpiredOpen(true);
      } else {
        toast.error("Could not generate the PDF. Please try again.");
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleRequestCopy = async () => {
    setRequesting(true);
    try {
      await requestPdfCopy(id);
      toast.success("If this assessment exists, a fresh link has been emailed to you.");
      setExpiredOpen(false);
    } catch {
      toast.error("Could not send a new copy right now. Please try again later.");
    } finally {
      setRequesting(false);
    }
  };

  const goHome = () => {
    window.location.href = HOME_URL;
  };

  if (error) {
    return (
      <div className="min-h-screen rd-bg text-white">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h2 className="text-2xl font-bold">Blueprint not available</h2>
          <p className="text-white/60 mt-3">
            This blueprint could not be found or requires lead capture.
          </p>
          <Button
            onClick={() => navigate("/assessment")}
            className="mt-6 bg-[#1E6BFF] hover:bg-[#3B82F6] rounded-xl"
          >
            Start a new assessment
          </Button>
          <div className="mt-3">
            <Button variant="ghost" onClick={goHome} data-testid="blueprint-error-home-button" className="text-white/50 hover:text-white hover:bg-white/5">
              Return to Rendeavored.com
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen rd-bg text-white">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="animate-spin text-[#1E6BFF]" size={28} />
        </div>
      </div>
    );
  }

  const r = data.result;
  const showPricing = !!data.show_pricing;
  const implEstimate = r.executive_dashboard?.implementation_estimate || "2\u20134 weeks";

  return (
    <div className="min-h-screen rd-bg text-white">
      <Header />

      {/* Hero summary */}
      <section className="relative overflow-hidden rd-noise border-b border-white/10">
        <div className="absolute inset-0 rd-glow pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#1E6BFF]/30 bg-[#1E6BFF]/10 px-3 py-1.5 text-xs text-[#66B2FF] mb-4">
            <Sparkles size={14} /> Your Personalized Workforce Blueprint
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {r.industry_label} team, engineered for your goals
          </h1>

          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            <div className="rounded-xl border border-white/10 p-5" style={{ backgroundColor: "#0F1424" }}>
              <div className="flex items-center gap-2 text-white/55 text-sm"><Users size={16} /> {showPricing ? "Team seats" : "Dedicated professionals"}</div>
              <div className="text-3xl font-bold mt-2 tabular-nums" data-testid="blueprint-total-headcount">{r.total_headcount}</div>
            </div>
            {showPricing ? (
              <>
                <div className="rounded-xl border border-white/10 p-5" style={{ backgroundColor: "#0F1424" }}>
                  <div className="flex items-center gap-2 text-white/55 text-sm"><Wallet size={16} /> Estimated Monthly Investment</div>
                  <div className="text-2xl font-bold mt-2 tabular-nums" data-testid="blueprint-total-investment-range">
                    {fmtInvestment(r.investment_low, r.investment_high)}
                  </div>
                </div>
                <div className="rounded-xl border border-[#1E6BFF]/25 p-5" style={{ backgroundColor: "#101a33" }}>
                  <div className="flex items-center gap-2 text-[#66B2FF] text-sm"><TrendingDown size={16} /> Estimated Annual Savings</div>
                  <div className="text-2xl font-bold mt-2 tabular-nums text-[#66B2FF]">{fmtMoney(r.savings_annual)}</div>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-xl border border-white/10 p-5" style={{ backgroundColor: "#0F1424" }}>
                  <div className="flex items-center gap-2 text-white/55 text-sm"><Clock size={16} /> Estimated implementation</div>
                  <div className="text-2xl font-bold mt-2" data-testid="blueprint-implementation">{implEstimate}</div>
                </div>
                <div className="rounded-xl border border-[#1E6BFF]/25 p-5" style={{ backgroundColor: "#101a33" }}>
                  <div className="flex items-center gap-2 text-[#66B2FF] text-sm"><Target size={16} /> Operational focus</div>
                  <div className="text-2xl font-bold mt-2 text-[#66B2FF]">{r.industry_label}</div>
                </div>
              </>
            )}
          </div>

          <div className="mt-6">
            <Button
              data-testid="blueprint-download-pdf-button"
              onClick={handleDownload}
              disabled={downloading}
              className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-12 px-6 text-base shadow-[0_10px_30px_rgba(30,107,255,0.25)]"
            >
              {downloading ? (
                <><Loader2 className="animate-spin mr-2" size={18} /> Preparing PDF...</>
              ) : (
                <><Download size={18} className="mr-2" /> Download My Workforce Blueprint</>
              )}
            </Button>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-14">
        {/* Executive summary dashboard (headline numbers) */}
        <ExecutiveDashboard result={r} showPricing={showPricing} />

        {/* Executive narrative */}
        <section>
          <h2 className="text-2xl font-bold mb-4">Executive summary</h2>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-xl border border-white/10 p-6 sm:p-8"
            style={{ backgroundColor: "#0F1424" }}
            data-testid="blueprint-narrative"
          >
            {(data.narrative || "").split("\n").filter(Boolean).map((para, i) => (
              <p key={i} className="text-white/80 leading-relaxed mb-4 last:mb-0">
                {para}
              </p>
            ))}
          </motion.div>
          <p className="text-white/40 text-xs mt-3 leading-relaxed" data-testid="blueprint-method-note">
            {METHOD_NOTE}
          </p>
        </section>
        <section>
          <h2 className="text-2xl font-bold mb-1">Your recommended team</h2>
          <p className="text-white/60 mb-6">Each role selected to solve your specific pain points.</p>
          <div className="grid md:grid-cols-2 gap-4" data-testid="blueprint-roles">
            {r.roles.map((role, i) => (
              <RoleCard key={role.key} role={role} index={i} showPricing={showPricing} />
            ))}
          </div>
          {showPricing && (
            <p className="text-white/40 text-xs mt-4 leading-relaxed" data-testid="blueprint-pricing-disclaimer">
              {PRICING_DISCLAIMER}
            </p>
          )}
        </section>

        {/* Customized Investment Proposal callout (shown when public pricing is hidden) */}
        {!showPricing && (
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="blueprint-investment-proposal"
            className="rounded-2xl border border-[#1E6BFF]/30 p-6 sm:p-8"
            style={{ backgroundColor: "#101a33" }}
          >
            <div className="flex items-center gap-3 mb-4">
              <span className="h-10 w-10 rounded-xl bg-[#1E6BFF]/15 text-[#66B2FF] flex items-center justify-center shrink-0">
                <FileText size={20} />
              </span>
              <h2 className="text-2xl font-bold">Customized Investment Proposal</h2>
            </div>
            <p className="text-white/80 leading-relaxed">
              Every organization is different. Your recommended team, implementation approach, and
              ongoing support model will be tailored to your business objectives, operational
              workflows, and growth plans. Because of this, we do not generate standardized pricing
              within your Workforce Blueprint. Instead, we prepare a customized investment proposal
              after reviewing your assessment together.
            </p>
            <p className="text-white/80 leading-relaxed mt-4 font-medium">
              During your complimentary Workforce Strategy Call, we&apos;ll:
            </p>
            <ul className="mt-3 space-y-2">
              {[
                "Review your recommended team structure and role responsibilities",
                "Validate your operational priorities and business objectives",
                "Discuss implementation timelines and onboarding approach",
                "Answer questions about our global talent model and managed support services",
                "Present a customized investment proposal tailored to your organization",
              ].map((item) => (
                <li key={item} className="text-white/75 text-sm flex items-start gap-2.5">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#66B2FF] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-[#66B2FF] font-semibold leading-relaxed mt-5">
              Our goal is simple: build the right team for your business&mdash;not sell a
              one-size-fits-all staffing package.
            </p>
          </motion.section>
        )}

        {/* Workforce Capacity Analysis (transparent methodology) */}
        <CapacityAnalysis result={r} showPricing={showPricing} />

        {/* Savings comparison (only when pricing is enabled) */}
        {showPricing ? (
        <section>
          <h2 className="text-2xl font-bold mb-1">Rendeavored vs. in-house</h2>
          <p className="text-white/60 mb-6">
            Estimated monthly cost comparison for an equivalent US in-house team.
          </p>          <div className="grid lg:grid-cols-3 gap-6 items-center">
            <div className="lg:col-span-2 rounded-xl border border-white/10 p-5" style={{ backgroundColor: "#0F1424" }}>
              <SavingsChart recommended={r.investment_midpoint} inhouse={r.inhouse_equivalent_monthly} />
            </div>
            <div className="rounded-xl border border-[#1E6BFF]/25 p-6" style={{ backgroundColor: "#101a33" }}>
              <div className="text-white/60 text-sm">You could save approximately</div>
              <div className="text-3xl font-bold text-[#66B2FF] mt-1 tabular-nums">
                {fmtMoney(r.savings_monthly)}<span className="text-base text-white/50">/mo</span>
              </div>
              <div className="text-white/60 text-sm mt-4">Annually, that&apos;s up to</div>
              <div className="text-2xl font-bold text-white mt-1 tabular-nums">{fmtMoney(r.savings_annual)}</div>
            </div>
          </div>
          <p className="text-white/40 text-xs mt-4 leading-relaxed">{PRICING_DISCLAIMER}</p>
        </section>
        ) : (
        <section data-testid="blueprint-team-summary">
          <h2 className="text-2xl font-bold mb-1">Team summary</h2>
          <p className="text-white/60 mb-6">A thoughtfully designed operating team for your goals.</p>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="rounded-xl border border-white/10 p-6" style={{ backgroundColor: "#0F1424" }}>
              <div className="flex items-center gap-2 text-white/55 text-sm mb-4"><Users size={16} /> Team composition</div>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-white tabular-nums">{r.total_headcount}</span>
                <span className="text-white/60">dedicated professionals</span>
              </div>
              <div className="mt-4 flex items-center gap-2 text-white/55 text-sm"><Clock size={15} /> Implementation timeline</div>
              <div className="text-xl font-bold text-white mt-1">{implEstimate}</div>
            </div>
            <div className="rounded-xl border border-[#1E6BFF]/25 p-6" style={{ backgroundColor: "#101a33" }}>
              <div className="flex items-center gap-2 text-[#66B2FF] text-sm mb-3"><Target size={16} /> Operational benefits</div>
              <ul className="space-y-2">
                {["Increased responsiveness", "Reduced administrative burden", "Improved process consistency", "Scalable support model"].map((b) => (
                  <li key={b} className="text-white/75 text-sm flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#66B2FF]" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
        )}

        {/* Roadmap */}
        <section>
          <h2 className="text-2xl font-bold mb-1">Implementation roadmap</h2>
          <p className="text-white/60 mb-6">From kickoff to a fully optimized team.</p>
          <Roadmap phases={r.roadmap} />
        </section>

        {/* Final CTA: Founder note + consultation (reusable component) */}
        <ConsultationCTA />

        {/* Secondary actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <Button
            data-testid="blueprint-download-pdf-cta"
            onClick={handleDownload}
            disabled={downloading}
            variant="secondary"
            className="bg-[#121A2E] border border-white/10 hover:border-white/25 text-white rounded-xl"
          >
            <Download size={16} className="mr-2" /> Download PDF
          </Button>
          <Button variant="ghost" onClick={goHome} data-testid="blueprint-home-button" className="text-white/50 hover:text-white hover:bg-white/5">
            <ArrowLeft size={16} className="mr-1" /> Return to Rendeavored.com
          </Button>
        </div>
      </main>

      <Footer />

      {/* Expired / missing link -> request a fresh copy */}
      <Dialog open={expiredOpen} onOpenChange={setExpiredOpen}>
        <DialogContent className="bg-[#0F1424] border-white/10 text-white" data-testid="pdf-expired-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Your download link has expired</DialogTitle>
          </DialogHeader>
          <p className="text-white/65 text-sm">
            For your privacy, blueprint download links expire. We can email a fresh,
            secure link to the address you used during the assessment.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-2">
            <Button
              data-testid="pdf-request-copy-button"
              onClick={handleRequestCopy}
              disabled={requesting}
              className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl"
            >
              {requesting ? <><Loader2 className="animate-spin mr-2" size={16} /> Sending...</> : <><Mail size={16} className="mr-2" /> Request a New Copy</>}
            </Button>
            <Button variant="ghost" onClick={() => setExpiredOpen(false)} className="text-white/60 hover:text-white hover:bg-white/5">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
